import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { HistoryManager } from "./HistoryManager";
import { deleteObj } from "../core/delete";
import {
  DictIterType,
  OpenMode,
  PlotPaperUnits,
  PlotType,
} from "../core/constants";
import { openAs } from "../core/oda";
import { selectionService } from "../selection";
import { Logger } from "@/logger";

export interface LayoutManagerDeps {
  viewer: DRDrawingViewer;
  history: HistoryManager;
  applyOverlayTheme: (backgroundColor: number) => void;
}

export class LayoutManager {
  private viewer: DRDrawingViewer;
  private history: HistoryManager;
  private applyOverlayTheme: (backgroundColor: number) => void;

  constructor({ viewer, history, applyOverlayTheme }: LayoutManagerDeps) {
    this.viewer = viewer;
    this.history = history;
    this.applyOverlayTheme = applyOverlayTheme;
  }

  // ==================== Query ====================

  getNames(): string[] {
    if (!this.viewer.database) return [];
    try {
      const dictId = this.viewer.database.getLayoutDictionaryId(false);
      const dict = openAs(
        dictId,
        this.viewer.module.OdDbDictionary,
        OpenMode.kForRead,
      );
      const layouts: { name: string; order: number }[] = [];

      const iter = dict.newIterator(DictIterType.kDictCollated);
      while (!iter.done()) {
        const name = iter.name().toString();
        const layoutId = iter.objectId();
        try {
          const layout = openAs(
            layoutId,
            this.viewer.module.OdDbLayout,
            OpenMode.kForRead,
          );
          layouts.push({ name, order: layout.getTabOrder() });
          deleteObj(layout);
        } catch {
          layouts.push({ name, order: 999 });
        }
        iter.next();
      }
      deleteObj(iter);
      deleteObj(dict);

      layouts.sort((a, b) => a.order - b.order);
      return layouts.map((l) => l.name);
    } catch (e) {
      Logger.warn(`LayoutManager.getNames failed: ${e}`);
      return ["Model"];
    }
  }

  getActive(): string {
    if (!this.viewer.database) return "Model";
    return this.viewer.database.findActiveLayout(true).toString();
  }

  // ==================== Switch ====================

  setActive(name: string): void {
    if (!this.viewer.database) return;

    // Dehighlight entities and clear overlay handles before switching.
    selectionService.clear();

    const nameStr = new this.viewer.module.OdString(name);
    this.viewer.database.setCurrentLayout(nameStr);
    deleteObj(nameStr);

    // Paper space keeps its own light background (see getEffectiveBackgroundColor).
    const bgColor = this.viewer.getEffectiveBackgroundColor();
    this.viewer.applyLayoutSwitch(bgColor);
    // Rulers and measurement labels must follow the background actually rendered.
    this.applyOverlayTheme(bgColor);

    // Clear again after device recreation — applyLayoutSwitch may trigger
    // internal ODA callbacks that cause the overlay to redraw stale handles.
    selectionService.clear();

    this.history.markDirty();

    if (name !== "Model") {
      this._logDiagnostic(name);
    }
  }

  // ==================== Create ====================

  create(name: string, widthMm: number, heightMm: number): void {
    if (!this.viewer.database) return;

    const uniqueName = this._uniqueName(name);

    // Create the layout record
    const nameStr = new this.viewer.module.OdString(uniqueName);
    const emptyId = new this.viewer.module.OdDbObjectId();
    const layoutId = this.viewer.database.createLayout(nameStr, emptyId);
    deleteObj(emptyId);
    deleteObj(nameStr);

    // Configure plot settings (refreshLists must come first)
    try {
      const layout = openAs(
        layoutId,
        this.viewer.module.OdDbLayout,
        OpenMode.kForWrite,
      );
      const validator = this.viewer.services.plotSettingsValidator();
      validator.refreshLists(layout);
      try {
        validator.setDefaultPlotConfig(layout);
      } catch (e) {
        Logger.warn(`LayoutManager.create: setDefaultPlotConfig failed: ${e}`);
      }
      validator.setClosestMediaName(
        layout,
        widthMm,
        heightMm,
        PlotPaperUnits.kMillimeters,
        false,
      );
      validator.setPlotPaperUnits(layout, PlotPaperUnits.kMillimeters);
      validator.setPlotType(layout, PlotType.kLayout);
      validator.setPlotCentered(layout, true);
      validator.setPlotOrigin(layout, 0, 0);

      // Set layout limits so ODA knows the paper boundary in world coordinates
      const ext =
        this.viewer.module.OdDbPlotSettings.paperSpaceLayoutSize(layout);
      if (ext.isValidExtents()) {
        const minPt = ext.minPoint();
        const maxPt = ext.maxPoint();
        layout.setLIMMIN(minPt);
        layout.setLIMMAX(maxPt);
        deleteObj(minPt);
        deleteObj(maxPt);
      }
      deleteObj(ext);
      deleteObj(layout);
    } catch (e) {
      Logger.warn(`LayoutManager.create: plot settings failed: ${e}`);
    }

    // Activate — creates the overall viewport and renders the paper
    this.setActive(uniqueName);
    this.viewer.forceRegen();
  }

  // ==================== Delete ====================

  delete(name: string): void {
    if (!this.viewer.database || name === "Model") return;
    if (this.getActive() === name) {
      this.setActive("Model");
    }
    const nameStr = new this.viewer.module.OdString(name);
    this.viewer.database.deleteLayout(nameStr);
    deleteObj(nameStr);
    this.history.markDirty();
  }

  // ==================== Rename ====================

  rename(oldName: string, newName: string): void {
    if (!this.viewer.database || oldName === "Model" || !newName.trim()) return;
    const wasActive = this.getActive() === oldName;
    const oldStr = new this.viewer.module.OdString(oldName);
    const newStr = new this.viewer.module.OdString(newName.trim());
    this.viewer.database.renameLayout(oldStr, newStr);
    deleteObj(oldStr);
    deleteObj(newStr);
    this.history.markDirty();
    if (wasActive) {
      this.setActive(newName.trim());
    }
  }

  // ==================== Duplicate ====================

  /**
   * Clone a layout and switch to the copy.
   * @returns The name used for the clone.
   */
  duplicate(name: string): string {
    if (!this.viewer.database) return name;

    const existing = new Set(this.getNames());
    let candidate = `${name} (2)`;
    let n = 2;
    while (existing.has(candidate)) {
      n++;
      candidate = `${name} (${n})`;
    }

    const nameStr = new this.viewer.module.OdString(name);
    const layoutId = this.viewer.database.findLayoutNamed(nameStr);
    deleteObj(nameStr);
    if (layoutId.isNull()) return name;

    const layout = openAs(
      layoutId,
      this.viewer.module.OdDbLayout,
      OpenMode.kForRead,
    );
    const tabOrder = layout.getTabOrder();
    const odaManager = this.viewer.services.layoutManager();
    const newNameStr = new this.viewer.module.OdString(candidate);
    odaManager.cloneLayout(
      this.viewer.database,
      layout,
      newNameStr,
      tabOrder + 1,
    );
    deleteObj(layout);
    deleteObj(newNameStr);

    this.setActive(candidate);
    return candidate;
  }

  // ==================== Private helpers ====================

  private _uniqueName(base: string): string {
    const existing = new Set(this.getNames());
    let name = base;
    let n = 2;
    while (existing.has(name)) {
      name = `${base} (${n++})`;
    }
    return name;
  }

  private _logDiagnostic(layoutName: string): void {
    if (!this.viewer.database) return;
    try {
      const layoutId = this.viewer.database.currentLayoutId();
      const dbLayout = openAs(
        layoutId,
        this.viewer.module.OdDbLayout,
        OpenMode.kForRead,
      );
      const mediaName = dbLayout.getCanonicalMediaName();
      const ext =
        this.viewer.module.OdDbPlotSettings.paperSpaceLayoutSize(dbLayout);
      const minPt = ext.minPoint();
      const maxPt = ext.maxPoint();
      const pSpaceX = Math.abs(maxPt.x - minPt.x);
      const pSpaceY = Math.abs(maxPt.y - minPt.y);
      deleteObj(ext);
      deleteObj(minPt);
      deleteObj(maxPt);
      const ovpId = dbLayout.overallVportId();
      const ovp = openAs(
        ovpId,
        this.viewer.module.OdDbViewport,
        OpenMode.kForRead,
      );
      const ovpW = ovp.width();
      const ovpH = ovp.height();
      deleteObj(ovp);
      Logger.info(
        `[LayoutManager] "${layoutName}" — media: "${mediaName?.toString()}" INSUNITS=${this.viewer.database.getINSUNITS()} plotPaperUnits=${dbLayout.plotPaperUnits()} | ovp.width=${ovpW} ovp.height=${ovpH} | pSpaceX=${pSpaceX} pSpaceY=${pSpaceY}`,
      );
      if (mediaName && typeof (mediaName as any).delete === "function")
        deleteObj(mediaName as any);
      deleteObj(dbLayout);
    } catch (e) {
      Logger.warn(`LayoutManager._logDiagnostic failed: ${e}`);
    }
  }
}
